import { wrappedApiGet, wrappedApiPost, wrappedApiPut } from 'utils/http';
import { GetPaymentResponse } from '@payer-admin-ui/api/src/payments/responses/GetPaymentResponse';
import { PageResponse } from '@payer-admin-ui/api/src/utils/repository/PageResponse';
import { PaymentPageItem } from '@payer-admin-ui/api/src/payments/responses/PaymentPageItem';
import { ZonedDateTime } from '@payer-admin-ui/api/src/external/java/ZonedDateTime';
import { RefundAndCreditResponse } from '@payer-admin-ui/api/src/cardpayments/responses/RefundAndCreditResponse';
import { PaymentIdObject } from '@payer-admin-ui/components-v2/src/types/state';
import { SwishRefundRequest } from '@payer-admin-ui/api/src/swish/requests/SwishRefundRequest';
import { RefundRequest } from '@payer-admin-ui/api/src/cardpayments/requests/RefundRequest';
import { CaptureRequest } from '@payer-admin-ui/api/src/cardpayments/requests/CaptureRequest';
import { CaptureResponse } from '@payer-admin-ui/api/src/cardpayments/responses/CaptureResponse';
import { PaymentType } from '@payer-admin-ui/api/src/invoices/models/PaymentType';
import { ReleaseAuthorizationResponse } from '@payer-admin-ui/api/src/cardpayments/responses/ReleaseAuthorizationResponse';
import { PaymentSummaryResponse } from '@payer-admin-ui/api/src/payments/responses/PaymentSummaryResponse';
import { toCentsOrNull } from '@payer-admin-ui/components-v2/src/functions/numbers';
import { PaymentFilters } from '../views/Pages/payments/paymentsGlobalState';

const paymentAPI = {
  fetchById: async ({ id }: { id: number }): Promise<GetPaymentResponse> => {
    return await wrappedApiGet<GetPaymentResponse>(`/payments/${id}`).withObjectResponse();
  },
  fetchPaymentsPage: async ({
    page,
    pageSize,
    filters,
    sort,
  }: {
    page: number;
    pageSize: number;
    filters: PaymentFilters;
    sort?: string[];
  }): Promise<PageResponse<PaymentPageItem>> => {
    return wrappedApiGet<PageResponse<PaymentPageItem>>(`/payments`, {
      query: {
        offset: page * pageSize,
        pageSize,
        sort,
        statuses: filters.statuses,
        currencyCodes: filters.currencyCodes,
        paymentMethods: filters.paymentMethods,
        creationDateStart: filters.creationDateStart,
        creationDateEnd: filters.creationDateEnd,
        amountStart: toCentsOrNull(filters.amountStart),
        amountEnd: toCentsOrNull(filters.amountEnd),
        search: filters.search,
      },
    }).withObjectResponse();
  },
  fetchPaymentsSummary: async ({ filters }: { filters: PaymentFilters }): Promise<PaymentSummaryResponse> => {
    return wrappedApiGet<PaymentSummaryResponse>(`/payments/summary`, {
      query: {
        statuses: filters.statuses,
        currencyCodes: filters.currencyCodes,
        paymentMethods: filters.paymentMethods,
        creationDateStart: filters.creationDateStart,
        creationDateEnd: filters.creationDateEnd,
        amountStart: toCentsOrNull(filters.amountStart),
        amountEnd: toCentsOrNull(filters.amountEnd),
        search: filters.search,
      },
    }).withObjectResponse();
  },
  exportToPyramid: async ({
    creationDateStart,
    creationDateEnd,
  }: {
    creationDateStart: ZonedDateTime;
    creationDateEnd: ZonedDateTime;
  }): Promise<string> => {
    return wrappedApiGet<string>(`/payments/export/pyramid`, {
      query: {
        creationDateStart,
        creationDateEnd,
      },
    }).withStringResponse();
  },
  actions: {
    captureCardPayment: async ({
      paymentId,
      amount,
      type,
      releaseRemainingFunds,
      transactionDescription,
    }: PaymentIdObject & CaptureRequest): Promise<CaptureResponse> => {
      if (type === PaymentType.CARD) {
        return wrappedApiPost<CaptureResponse>(`/payments/${paymentId}/cards/capture`, {
          body: {
            amount,
            transactionDescription,
            releaseRemainingFunds,
          },
        }).withObjectResponse();
      } else {
        return wrappedApiPost<CaptureResponse>(`/payments/${paymentId}/cards/capture`, {
          body: {
            amount,
            transactionDescription,
            releaseRemainingFunds,
          },
        }).withObjectResponse();
      }
    },
    refundCardPayment: async ({
      paymentId,
      amount,
      transactionDescription,
    }: PaymentIdObject & RefundRequest): Promise<RefundAndCreditResponse> => {
      return wrappedApiPost<RefundAndCreditResponse>(`/payments/${paymentId}/cards/refund`, {
        body: {
          amount,
          transactionDescription,
        },
      }).withObjectResponse();
    },
    refundCloudPosPayment: async ({
      paymentId,
      amount,
      transactionDescription,
    }: PaymentIdObject & RefundRequest): Promise<RefundAndCreditResponse> => {
      return wrappedApiPost<RefundAndCreditResponse>(`/payments/${paymentId}/cloudpos/refund`, {
        body: {
          amount,
          transactionDescription,
        },
      }).withObjectResponse();
    },
    releaseAuthorization: async (paymentId: PaymentIdObject): Promise<ReleaseAuthorizationResponse> => {
      return wrappedApiPut<ReleaseAuthorizationResponse>(`/payments/${paymentId.paymentId}/cards/release`, {
        body: {},
      }).withObjectResponse();
    },
    refundSwishPayment: async ({
      paymentId,
      amount,
      message,
      externalReferenceId,
    }: PaymentIdObject & SwishRefundRequest): Promise<void> => {
      return wrappedApiPost(`/payments/${paymentId}/swish/refund`, {
        body: {
          amount,
          message,
          externalReferenceId,
        },
      }).withEmptyResponse();
    },
  },
};

export { paymentAPI };
